'use client';
import cn from 'classnames';
import { FormikHelpers, FormikErrors } from 'formik';
import { useContext, useState } from 'react';

import { CACHE_TIME } from '@/const/cache';

import './UpsertAddressForm.style.scss';

import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import Select from '../lib/InputField/Select';
import Text from '@/components/common/Text/Text';

import { UserContext } from '@/components/context/UserContext/UserContext';

import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import AddressInputField from '@/components/form/lib/InputField/AddressInputField';
import InputField from '@/components/form/lib/InputField/InputField';
import MaskedField from '@/components/form/lib/MaskedField/MaskedField';
import SubmitButton from '@/components/form/lib/SubmitButton/SubmitButton';

import { US_STATES } from '@/util/user_client';
import {
  isValidAddress,
  isValidCityName,
  isValidName,
  isValidPhone
} from '@/util/regexp';
import { phoneMaskPipe } from '@/util/phone';

export type UpsertAddressFormValues = {
  addressId?: string;
  firstName: string;
  lastName: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
};

export type AddressValues = {
  type?: string;
  address_id?: string;
  name: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  line_1: string;
  line_2: string;
  city: string;
  postcode: string;
  region: string;
  country: string;
  isDefault: boolean;
  isBilling: boolean;
};

interface UpsertAddressFormProps
  extends Omit<
    BaseFormProps<UpsertAddressFormValues>,
    'children' | 'onSubmit'
  > {
  isDefault: boolean;
  onSubmit?: (
    values: UpsertAddressFormValues,
    actions: FormikHelpers<UpsertAddressFormValues>
  ) => void;
  onCancel?: () => void;
}

function defaultValidation(
  values: UpsertAddressFormValues
): FormikErrors<UpsertAddressFormValues> {
  const {
    addressId,
    firstName,
    lastName,
    address,
    address2,
    city,
    state,
    postalCode,
    phone
  } = values;
  const errors: FormikErrors<UpsertAddressFormValues> = {};

  const requiredError = 'This field is required.';

  if (!firstName || !isValidName(firstName)) {
    errors.firstName = requiredError;
  }
  if (!lastName || !isValidName(lastName)) {
    errors.lastName = requiredError;
  }
  if (!address || !isValidAddress(address)) {
    errors.address = 'Please enter a valid address';
  }
  if (!city || !isValidCityName(city)) {
    errors.city = 'Please enter a valid city';
  }
  if (!state) {
    errors.state = 'Please enter a valid state';
  }

  if (!postalCode || postalCode.length !== 5) {
    errors.postalCode = 'Please enter a valid zip code';
  }

  if (!phone) {
    errors.phone = 'A phone number is required';
  } else if (phone.length < 14) {
    /* Phone totals 14 with masked characters */
    errors.phone = 'Please enter a valid 10-digit phone number';
  } else if (!isValidPhone(phone)) {
    errors.phone = 'A valid phone number is required';
  }

  return errors;
}

export default function UpsertAddressForm(props: UpsertAddressFormProps) {
  const {
    className = '',
    initialValues,
    validate = defaultValidation,
    onSubmit,
    onSubmitSuccess,
    onCancel,
    validateOnBlur = true,
    validateOnChange = false,
    isDefault
  } = props;

  const [isSavingAddress, setSavingAddress] = useState<boolean>(false);
  const [generalError, setGeneralError] = useState<string | null>();

  const { actions: userActions } = useContext(UserContext);

  function parseAddress(address: string): Partial<UpsertAddressFormValues> {
    // Sometimes address can be undefined if a user doesn't trigger the auto complete
    // (hit enter without selecting a suggested address)
    // If that's the case, return an object where the props are empty strings to trigger formik validation
    if (!address) {
      return {
        address: '',
        city: '',
        state: '',
        postalCode: ''
      } as Partial<UpsertAddressFormValues>;
    }

    const addressArray = address.split(',');
    const fullAddress: Partial<UpsertAddressFormValues> = {
      address: addressArray[0] || '',
      city: addressArray[1]?.trim() || '',
      state: addressArray[2]?.trim().split(' ')[0] || '',
      postalCode: addressArray[2]?.trim().split(' ')[1] || ''
    };

    return fullAddress;
  }

  const handleSubmit = async (values: UpsertAddressFormValues) => {
    let addressPayload = {
      type: 'address',
      name: `${values.firstName} ${values.lastName}`,
      first_name: values.firstName,
      last_name: values.lastName,
      phone_number: values.phone,
      line_1: values.address,
      line_2: values.address2,
      city: values.city,
      postcode: values.postalCode,
      region: values.state,
      country: 'US',
      isDefault,
      isBilling: false
    } as AddressValues;
    let addressResponse;

    setGeneralError(null);
    setSavingAddress(true);

    addressResponse = await fetch(`/api/user/addresses`, {
      method: 'POST',
      body: JSON.stringify(addressPayload),
      next: {
        revalidate: CACHE_TIME.NO_CACHE
      }
    });

    const updateSuccess = await addressResponse.json();

    if (updateSuccess.success) {
      await userActions.refresh();
      onSubmitSuccess?.(values);
    } else {
      setGeneralError(
        'There was an error updating your information. Please double check and try again.'
      );
    }
    setSavingAddress(false);
  };

  return (
    <Form<UpsertAddressFormValues>
      className={cn('UpsertAddressForm', className)}
      initialValues={initialValues}
      validate={validate}
      // If an 'onSubmit function' is passed in, that function will override
      // this component's 'handleSubmit' function.
      onSubmit={onSubmit || handleSubmit}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
    >
      {onCancel && (
        <div className='UpsertAddressForm-header'>
          <Text variant='title-xxs-500-upper'>Edit Shipping</Text>
          <Text
            className={cn('Link', 'link-s', 'compact', 'underline')}
            variant='text-s'
            onClick={onCancel}
          >
            Cancel
          </Text>
        </div>
      )}

      {initialValues.addressId && (
        <InputField
          name='addressId'
          type='hidden'
        />
      )}

      <InputField
        name='firstName'
        label={'First Name'}
        type='text'
      />
      <InputField
        name='lastName'
        label={'Last Name'}
        type='text'
      />

      <AddressInputField
        label={'Address'}
        onAddressSelected={(formikContext, formattedAddress) => {
          const { address, city, state, postalCode } =
            parseAddress(formattedAddress);

          formikContext.setFieldValue('address', address, false);
          formikContext.setFieldValue('city', city, false);
          formikContext.setFieldValue('state', state, false);
          formikContext.setFieldValue('postalCode', postalCode, false);
        }}
        name='address'
        defaultValue={initialValues.address}
      />

      <InputField
        name='address2'
        label={'Apt/Unit/Suite (optional)'}
        type='text'
      />

      <div className='UpsertAddressForm-cityState'>
        <InputField
          className='UpsertAddressForm-city'
          name='city'
          label={'City'}
          type='text'
        />
        <div className='UpsertAddressForm-state'>
          <Select
            name='state'
            label={'State'}
            options={US_STATES}
          />
        </div>
      </div>

      <MaskedField
        mask={[/\d/, /\d/, /\d/, /\d/, /\d/]}
        label={'Postal Code'}
        name='postalCode'
        guide={false}
      />
      <MaskedField
        mask={[
          '(',
          /[1-9]/,
          /\d/,
          /\d/,
          ')',
          ' ',
          /[2-9]/,
          /\d/,
          /\d/,
          '-',
          /\d/,
          /\d/,
          /\d/,
          /\d/
        ]}
        pipe={phoneMaskPipe}
        label={'Phone Number'}
        name='phone'
        guide={false}
        type='tel'
        autoComplete='tel'
      />

      <SubmitButton
        variant='light'
        size='small'
        disabled={isSavingAddress}
      >
        Save
        {isSavingAddress && (
          <>
            {' '}
            <LoadingSpinner size={10} />
          </>
        )}
      </SubmitButton>

      {generalError && (
        <Text className='UpdatePasswordForm-error'>{generalError}</Text>
      )}
    </Form>
  );
}
